/**
 * MarkdownView — a tiny, dependency-free renderer for the instructions.md preview. We deliberately do
 * NOT add a markdown library (no new top-level dep): the editor preview only needs headings (#, ##,
 * ###), bullet list items (-, *), and paragraphs, which we segment line-by-line. Anything else renders
 * as plain text. No `dangerouslySetInnerHTML` — every line is plain React text, so the lab's content
 * is never interpreted as HTML (XSS-safe by construction).
 */
import { Fragment, type ReactNode } from "react";

export interface MarkdownViewProps {
  source: string;
  /** Shown when the source is empty (the live-preview placeholder). */
  emptyText?: string;
}

interface Block {
  key: string;
  node: ReactNode;
}

function renderBlocks(source: string): Block[] {
  const lines = source.replace(/\r\n/g, "\n").split("\n");
  const blocks: Block[] = [];
  let bullets: string[] = [];
  let paragraph: string[] = [];

  const flushBullets = (i: number) => {
    if (bullets.length === 0) return;
    const items = [...bullets];
    blocks.push({
      key: `ul-${i}`,
      node: (
        <ul style={{ margin: "0 0 var(--nv-s-2)", paddingInlineStart: "var(--nv-s-5)" }}>
          {items.map((b, bi) => (
            <li key={bi} style={{ marginBlock: "var(--nv-s-1)" }}>
              {b}
            </li>
          ))}
        </ul>
      ),
    });
    bullets = [];
  };

  const flushParagraph = (i: number) => {
    if (paragraph.length === 0) return;
    const text = paragraph.join(" ");
    blocks.push({
      key: `p-${i}`,
      node: <p style={{ margin: "0 0 var(--nv-s-2)" }}>{text}</p>,
    });
    paragraph = [];
  };

  lines.forEach((raw, i) => {
    const line = raw.trimEnd();
    const heading = /^(#{1,3})\s+(.*)$/.exec(line);
    const bullet = /^[-*]\s+(.*)$/.exec(line);

    if (heading) {
      flushBullets(i);
      flushParagraph(i);
      const level = heading[1]!.length;
      const text = heading[2]!;
      const fs = level === 1 ? "var(--nv-fs-h2)" : level === 2 ? "var(--nv-fs-h3)" : "var(--nv-fs-title)";
      blocks.push({
        key: `h-${i}`,
        node: (
          <div style={{ fontSize: fs, fontWeight: 600, margin: "var(--nv-s-2) 0 var(--nv-s-1)" }}>
            {text}
          </div>
        ),
      });
      return;
    }
    if (bullet) {
      flushParagraph(i);
      bullets.push(bullet[1]!);
      return;
    }
    if (line.trim() === "") {
      flushBullets(i);
      flushParagraph(i);
      return;
    }
    flushBullets(i);
    paragraph.push(line);
  });

  flushBullets(lines.length);
  flushParagraph(lines.length);
  return blocks;
}

export function MarkdownView({ source, emptyText }: MarkdownViewProps) {
  const trimmed = source.trim();
  if (!trimmed) {
    return (
      <p style={{ margin: 0, color: "var(--nv-steel)", fontStyle: "italic" }}>{emptyText ?? ""}</p>
    );
  }
  const blocks = renderBlocks(source);
  return (
    <div style={{ fontSize: "var(--nv-fs-body)", color: "var(--nv-ink)", lineHeight: 1.6 }}>
      {blocks.map((b) => (
        <Fragment key={b.key}>{b.node}</Fragment>
      ))}
    </div>
  );
}
